/*!
  =========================================================
  * Muse Ant Design Dashboard - v1.0.0
  =========================================================
  * Product Page: https://www.creative-tim.com/product/muse-ant-design-dashboard
  * Copyright 2021 Creative Tim (https://www.creative-tim.com)
  * Licensed under MIT (https://github.com/creativetimofficial/muse-ant-design-dashboard/blob/main/LICENSE.md)
  * Coded by Creative Tim
  =========================================================
  * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { Menu, Button, Spin, Badge, Tooltip } from "antd";
import { NavLink, useLocation } from "react-router-dom";
import { useAdminPermissions } from "../../context/AdminContext";
import { useEffect, useState, useMemo } from "react";
import { AreaChartOutlined, CopyOutlined } from "@ant-design/icons";

function Sidenav({ color }) {
  const { pathname } = useLocation();
  const page = pathname.replace("/", "");
  const { hasRouteAccess, admin, loading, permissions } = useAdminPermissions();
  const [accessibleMenuItems, setAccessibleMenuItems] = useState([]);

  // Configuration de tous les éléments de menu avec leurs permissions
  const allMenuItems = useMemo(
    () => [
      {
        key: "1",
        route: "/dashboard",
        page: "dashboard",
        label: "Dashboard",
        category: "system",
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 4C3 3.44772 3.44772 3 4 3H16C16.5523 3 17 3.44772 17 4V6C17 6.55228 16.5523 7 16 7H4C3.44772 7 3 6.55228 3 6V4Z"
              fill={color}
            />
            <path
              d="M3 10C3 9.44771 3.44772 9 4 9H10C10.5523 9 11 9.44771 11 10V16C11 16.5523 10.5523 17 10 17H4C3.44772 17 3 16.5523 3 16V10Z"
              fill={color}
            />
            <path
              d="M14 9C13.4477 9 13 9.44771 13 10V16C13 16.5523 13.4477 17 14 17H16C16.5523 17 17 16.5523 17 16V10C17 9.44771 16.5523 9 16 9H14Z"
              fill={color}
            />
          </svg>
        ),
      },
      {
        key: "2",
        route: "/users",
        page: "users",
        label: "Utilisateurs",
        category: "users",
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM12 7C12 8.10457 11.1046 9 10 9C8.89543 9 8 8.10457 8 7C8 5.89543 8.89543 5 10 5C11.1046 5 12 5.89543 12 7ZM9.99993 11C7.98239 11 6.24394 12.195 5.45374 13.9157C6.55403 15.192 8.18265 16 9.99998 16C11.8173 16 13.4459 15.1921 14.5462 13.9158C13.756 12.195 12.0175 11 9.99993 11Z"
              fill={color}
            />
          </svg>
        ),
      },
      {
        key: "9",
        route: "/organisations",
        page: "organisations",
        label: "Compte Business",
        category: "users",
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6 2C5.44772 2 5 2.44772 5 3V4H4C2.89543 4 2 4.89543 2 6V16C2 17.1046 2.89543 18 4 18H16C17.1046 18 18 17.1046 18 16V6C18 4.89543 17.1046 4 16 4H15V3C15 2.44772 14.5523 2 14 2C13.4477 2 13 2.44772 13 3V4H7V3C7 2.44772 6.55228 2 6 2ZM6 7C5.44772 7 5 7.44772 5 8C5 8.55228 5.44772 9 6 9H14C14.5523 9 15 8.55228 15 8C15 7.44772 14.5523 7 14 7H6Z"
              fill={color}
            />
          </svg>
        ),
      },
      {
        key: "3",
        route: "/billing",
        page: "billing",
        label: "Portefeuille",
        category: "financial",
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 4C2.89543 4 2 4.89543 2 6V7H18V6C18 4.89543 17.1046 4 16 4H4Z"
              fill={color}
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M18 9H2V14C2 15.1046 2.89543 16 4 16H16C17.1046 16 18 15.1046 18 14V9ZM4 13C4 12.4477 4.44772 12 5 12H6C6.55228 12 7 12.4477 7 13C7 13.5523 6.55228 14 6 14H5C4.44772 14 4 13.5523 4 13ZM9 12C8.44772 12 8 12.4477 8 13C8 13.5523 8.44772 14 9 14H10C10.5523 14 11 13.5523 11 13C11 12.4477 10.5523 12 10 12H9Z"
              fill={color}
            />
          </svg>
        ),
      },
      {
        key: "4",
        route: "/transactions",
        page: "transactions",
        label: "Transactions",
        category: "financial",
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M3 6C3 4.34315 4.34315 3 6 3H16C16.3788 3 16.725 3.214 16.8944 3.55279C17.0638 3.89157 17.0273 4.29698 16.8 4.6L14.25 8L16.8 11.4C17.0273 11.703 17.0638 12.1084 16.8944 12.4472C16.725 12.786 16.3788 13 16 13H6C5.44772 13 5 13.4477 5 14V17C5 17.5523 4.55228 18 4 18C3.44772 18 3 17.5523 3 17V6Z"
              fill={color}
            />
          </svg>
        ),
      },
      {
        key: "5",
        route: "/airtime",
        page: "airtime",
        label: "Smart Kabine",
        category: "financial",
        icon: (
          <img
            src="/phone-call.png"
            alt=""
            style={{ height: "14px", width: "14px" }}
          />
        ),
      },
      {
        key: "10",
        route: "/supply",
        page: "supply",
        label: "Approvionnements",
        category: "financial",
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 2C8.44772 2 8 2.44772 8 3C8 3.55228 8.44772 4 9 4H11C11.5523 4 12 3.55228 12 3C12 2.44772 11.5523 2 11 2H9Z"
              fill={color}
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M4 5C4 3.89543 4.89543 3 6 3C6 4.65685 7.34315 6 9 6H11C12.6569 6 14 4.65685 14 3C15.1046 3 16 3.89543 16 5V16C16 17.1046 15.1046 18 14 18H6C4.89543 18 4 17.1046 4 16V5ZM7 9C6.44772 9 6 9.44772 6 10C6 10.5523 6.44772 11 7 11H7.01C7.56228 11 8.01 10.5523 8.01 10C8.01 9.44772 7.56228 9 7.01 9H7ZM10 9C9.44772 9 9 9.44772 9 10C9 10.5523 9.44772 11 10 11H13C13.5523 11 14 10.5523 14 10C14 9.44772 13.5523 9 13 9H10ZM7 13C6.44772 13 6 13.4477 6 14C6 14.5523 6.44772 15 7 15H7.01C7.56228 15 8.01 14.5523 8.01 14C8.01 13.4477 7.56228 13 7.01 13H7ZM10 13C9.44772 13 9 13.4477 9 14C9 14.5523 9.44772 15 10 15H13C13.5523 15 14 14.5523 14 14C14 13.4477 13.5523 13 13 13H10Z"
              fill={color}
            />
          </svg>
        ),
      },
      {
        key: "11",
        route: "/qr-code",
        page: "qr-code",
        label: "Qr-code",
        category: "system",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 14 14"
          >
            <path
              d="M0,2A2,2,0,0,1,2,0H8a2,2,0,0,1,2,2V8a2,2,0,0,1-2,2H2A2,2,0,0,1,0,8Z"
              transform="translate(4 4)"
              fill={color}
            />
            <path
              d="M2,0A2,2,0,0,0,0,2V8a2,2,0,0,0,2,2V4A2,2,0,0,1,4,2h6A2,2,0,0,0,8,0Z"
              fill={color}
            />
          </svg>
        ),
      },
      {
        key: "12",
        route: "/commercials",
        page: "commercials",
        label: "Commercials",
        category: "commercial",
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM12 7C12 8.10457 11.1046 9 10 9C8.89543 9 8 8.10457 8 7C8 5.89543 8.89543 5 10 5C11.1046 5 12 5.89543 12 7ZM9.99993 11C7.98239 11 6.24394 12.195 5.45374 13.9157C6.55403 15.192 8.18265 16 9.99998 16C11.8173 16 13.4459 15.1921 14.5462 13.9158C13.756 12.195 12.0175 11 9.99993 11Z"
              fill={color}
            />
          </svg>
        ),
      },
      {
        key: "13",
        route: "/verification",
        page: "verification",
        label: "KYC",
        category: "users",
        icon: (
          <img
            src="/kyc.png"
            alt=""
            style={{ height: "14px", width: "14px" }}
          />
        ),
      },
      {
        key: "14",
        route: "/admin-management",
        page: "admin-management",
        label: "Gestion Admins",
        category: "system",
        icon: (
          <img
            src="/admin-alt.png"
            alt=""
            style={{ height: "14px", width: "14px" }}
          />
        ),
      },
      {
        key: "15",
        route: "/organisations/limits",
        page: "organisations/limits",
        label: "Limites de Transaction",
        category: "limits",
        icon: (
          <AreaChartOutlined
            style={{
              height: "30px",
              width: "30px",
              fontSize: "20px",
              padding: "4px",
            }}
          />
        ),
      },
      {
        key: "16",
        route: "/organisation-verification",
        page: "organisation-verification",
        label: "KYB Organisations",
        category: "users",
        icon: (
          <CopyOutlined
            style={{
              height: "30px",
              width: "30px",
              fontSize: "20px",
              padding: "4px",
            }}
          />
        ),
      },
    ],

    [color],
  );

  // Filtrer les éléments du menu selon les permissions avec optimisation
  useEffect(() => {
    if (!loading && admin && permissions.allowedRoutes) {
      const filteredItems = allMenuItems.filter((item) =>
        hasRouteAccess(item.route),
      );
      setAccessibleMenuItems(filteredItems);
    }
  }, [loading, admin, permissions.allowedRoutes, allMenuItems, hasRouteAccess]);

  // Grouper les éléments par catégorie
  const groupedItems = useMemo(() => {
    const groups = {
      system: { label: "Système", items: [] },
      users: { label: "Utilisateurs & Organisations", items: [] },
      financial: { label: "Financier", items: [] },
      limits: { label: "Gestion des limites", items: [] },
      commercial: { label: "Commercial", items: [] },
    };

    accessibleMenuItems.forEach((item) => {
      if (groups[item.category]) {
        groups[item.category].items.push(item);
      }
    });

    return Object.entries(groups).filter(
      ([_, group]) => group.items.length > 0,
    );
  }, [accessibleMenuItems]);

  // Afficher un état de chargement si les permissions ne sont pas encore chargées
  if (loading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Spin tip="Chargement des permissions..." />
      </div>
    );
  }

  // Si aucun admin n'est connecté
  if (!admin) {
    return null;
  }

  return (
    <>
      <div
        className="brand"
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "20px 0",
        }}
      >
        <img
          src="/logo-A.jpg"
          alt="Logo"
          style={{
            width: "50%",
            height: "auto",
            borderRadius: "20%",
          }}
        />
      </div>

      {/* Menu avec groupes de catégories */}
      <div>
        {groupedItems.map(([category, group]) => (
          <div key={category}>
            {groupedItems.length > 1 && (
              <div
                style={{
                  padding: "8px 16px",
                  fontSize: "11px",
                  color: "#999",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginTop: "8px",
                }}
              >
                {group.label}
              </div>
            )}
            <Menu theme="light" mode="inline" selectedKeys={[page]}>
              {group.items.map((item) => (
                <Menu.Item key={item.page}>
                  <NavLink to={item.route}>
                    <span
                      className="icon"
                      style={{
                        background: page === item.page ? color : "",
                      }}
                    >
                      {item.icon}
                    </span>
                    <span className="label">{item.label}</span>
                  </NavLink>
                </Menu.Item>
              ))}
            </Menu>
          </div>
        ))}
      </div>
    </>
  );
}

export default Sidenav;
